import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, DatabaseBackup, Download, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

function BackupCard() {
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ["backup-status"],
    queryFn: async () => (await api.get("/admin/backup/status")).data,
  });
  const runBackup = useMutation({
    mutationFn: async () => (await api.post("/admin/backup/run")).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["backup-status"] });
      toast.success(`Backup created: ${res.archive}${res.offsite ? " (synced off-site)" : " (local only)"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backup failed"),
  });
  const download = async () => {
    try {
      const res = await api.get("/admin/backup/latest/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = status?.latest?.filename || "backup.archive.gz";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };
  const latest = status?.latest;
  const stale = status?.stale;

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="backup-card">
      <div className="label-eyebrow">Database Backup</div>
      <p className="text-sm text-slate-600 mt-1">
        Automated nightly backup (02:00 UTC) with an off-site copy in object storage. Keeps the latest 7.
      </p>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <div className={`text-sm ${stale ? "text-amber-700" : "text-slate-700"}`} data-testid="backup-last-time">
          {latest
            ? <>Last backup: <span className="font-semibold">{formatDateTime(latest.created_at)}</span>
              {" · "}{Math.round((latest.size_bytes || 0) / 1024)} KB{" · "}{latest.location}</>
            : "No backups yet."}
        </div>
        {stale && latest && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
            style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
            data-testid="backup-stale-warning"
          >
            <AlertTriangle className="h-3 w-3" /> No successful backup in over 24h
          </span>
        )}
      </div>
      <div className="mt-4 flex gap-2">
        <Button onClick={() => runBackup.mutate()} disabled={runBackup.isPending}
          className="bg-slate-900 hover:bg-slate-800" data-testid="backup-run-now">
          <DatabaseBackup className="h-4 w-4 mr-1.5" /> {runBackup.isPending ? "Running…" : "Run backup now"}
        </Button>
        <Button variant="outline" onClick={download} disabled={!latest} data-testid="backup-download-latest">
          <Download className="h-4 w-4 mr-1.5" /> Download latest
        </Button>
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between py-3.5 border-t border-slate-100">
      <div className="pr-6">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export default function SystemSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["system-settings"],
    queryFn: async () => (await api.get("/admin/system-settings")).data,
  });

  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm({ ...data }); }, [data]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const { dedup_key_field, ...payload } = form;
      // coerce numeric fields
      ["critical_dates_window_days", "sla_breach_days", "file_upload_max_mb"].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = Number(payload[k]);
      });
      return api.put("/admin/system-settings", payload);
    },
    onSuccess: (res) => {
      qc.setQueryData(["system-settings"], res.data);
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save"),
  });

  if (isLoading || !form) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl" data-testid="system-settings-page">
      <div className="label-eyebrow">Admin</div>
      <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">System Settings</h1>
      <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
        Unified configuration across all modules — alerts, SLAs, digests and upload limits.
      </p>

      <div className="mt-6 bg-white border border-slate-200 rounded-md px-5 pb-2">
        <Row label="Critical dates window (days)" hint="EMD / Bid / Auction alerts fire within this many days.">
          <Input type="number" min="1" value={form.critical_dates_window_days}
            onChange={(e) => set("critical_dates_window_days", e.target.value)}
            className="w-24 font-mono-num text-right" data-testid="set-critical-window" />
        </Row>
        <Row label="SLA breach threshold (days)" hint="Matches non-terminal beyond this are flagged.">
          <Input type="number" min="1" value={form.sla_breach_days}
            onChange={(e) => set("sla_breach_days", e.target.value)}
            className="w-24 font-mono-num text-right" data-testid="set-sla-days" />
        </Row>
        <Row label="Max upload size (MB)">
          <Input type="number" min="1" value={form.file_upload_max_mb}
            onChange={(e) => set("file_upload_max_mb", e.target.value)}
            className="w-24 font-mono-num text-right" data-testid="set-upload-mb" />
        </Row>
        <Row label="Time format">
          <Select value={form.time_format} onValueChange={(v) => set("time_format", v)}>
            <SelectTrigger className="w-36" data-testid="set-time-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="12-hour">12-hour</SelectItem>
              <SelectItem value="24-hour">24-hour</SelectItem>
            </SelectContent>
          </Select>
        </Row>

        <Row label="EMD T-3 alert" hint="3 days before EMD deadline.">
          <Switch checked={!!form.emd_alert_t3_enabled} onCheckedChange={(v) => set("emd_alert_t3_enabled", v)} data-testid="set-emd-t3" />
        </Row>
        <Row label="EMD T-1 alert" hint="1 day before EMD deadline.">
          <Switch checked={!!form.emd_alert_t1_enabled} onCheckedChange={(v) => set("emd_alert_t1_enabled", v)} data-testid="set-emd-t1" />
        </Row>
        <Row label="Bid T-2 alert" hint="2 days before bid submission.">
          <Switch checked={!!form.bid_alert_t2_enabled} onCheckedChange={(v) => set("bid_alert_t2_enabled", v)} data-testid="set-bid-t2" />
        </Row>

        <Row label="Morning digest" hint="Daily summary email to each user.">
          <Switch checked={!!form.morning_digest_enabled} onCheckedChange={(v) => set("morning_digest_enabled", v)} data-testid="set-digest-enabled" />
        </Row>
        <Row label="Digest time">
          <Input type="time" value={form.morning_digest_time}
            onChange={(e) => set("morning_digest_time", e.target.value)}
            className="w-32 font-mono-num" data-testid="set-digest-time" />
        </Row>
        <Row label="Digest channel">
          <Select value={form.morning_digest_channel} onValueChange={(v) => set("morning_digest_channel", v)}>
            <SelectTrigger className="w-36" data-testid="set-digest-channel"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Email">Email</SelectItem>
              <SelectItem value="Slack">Slack</SelectItem>
              <SelectItem value="WhatsApp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <Row label="Deduplication key" hint="Field used to detect duplicate inbound leads (read-only).">
          <Input value={form.dedup_key_field} disabled className="w-40 bg-slate-50 text-slate-500" data-testid="set-dedup-key" />
        </Row>
      </div>

      <div className="flex justify-end mt-5">
        <Button onClick={() => save.mutate()} disabled={save.isPending}
          className="bg-slate-900 hover:bg-slate-800" data-testid="save-settings-btn">
          <Save className="h-4 w-4 mr-1.5" /> Save settings
        </Button>
      </div>

      <BackupCard />
    </div>
  );
}
